// src/ConsumerApp/Screens/ProductDetails/components/ProductRailSection.js

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import CaretRight from '../../../../assets/icons/CaretRight.svg';
import ProductCard from '../../ProductsList/components/ProductCard';
import ProductOptionsModal from '../components/ProductOptionsModal';
import { guestBucketManager } from '../../../utils/guestBucketManager';
import {
  useGetBucketSummary,
  useGetProductVariants,
} from '../../../api/products/product.hooks';
import {
  useGetSavedList,
  useAddItemToSavedList,
  useRemoveItemFromSavedList,
} from '../../../api/savedList/savedList.hooks';

export default function ProductRailSection({
  title = 'Similar Products',
  subtitle = '',
  products = [],
  customerId,
  hubId,
  isLoading = false,
}) {
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Options Sheet State
  const [isOptionsSheetVisible, setIsOptionsSheetVisible] = useState(false);
  const [selectedProductWithOptions, setSelectedProductWithOptions] = useState([]);
  const [isOptionsAvailableForBuyNow, setIsOptionsAvailableForBuyNow] = useState(true);

  // 1. Guest Bucket Map
  const [guestBucketMap, setGuestBucketMap] = useState(() => guestBucketManager.getQuantityMap());
  useEffect(() => {
    const handleGuestChange = () => setGuestBucketMap(guestBucketManager.getQuantityMap());
    window.addEventListener('guest-bucket-change', handleGuestChange);
    return () => window.removeEventListener('guest-bucket-change', handleGuestChange);
  }, []);

  // 2. Saved Items & Bucket Queries
  const { data: bucketSummary } = useGetBucketSummary(customerId);
  const { data: savedList = [] } = useGetSavedList(customerId);
  const addItemToSavedMutation = useAddItemToSavedList();
  const removeItemFromSavedMutation = useRemoveItemFromSavedList();
  const { mutateAsync: fetchProductVariants } = useGetProductVariants();

  const savedItemsSet = useMemo(() => {
    const list = Array.isArray(savedList) ? savedList : savedList?.items || [];
    return new Set(list.map((item) => String(item.product_id || item.item_id || item._id)));
  }, [savedList]);

  const bucketItemsMap = useMemo(() => {
    const map = new Map();
    const buyNowItems = bucketSummary?.buy_now_bucket?.items || [];
    buyNowItems.forEach((bItem) => {
      if (bItem?.item_id) {
        map.set(String(bItem.item_id), {
          quantity: bItem.quantity,
          bucketId: bucketSummary?.buy_now_bucket?.bucket_id || bucketSummary?.buy_now_bucket?._id,
        });
      }
    });
    return map;
  }, [bucketSummary]);

  const getProductQuantity = (pid) => {
    if (customerId) return bucketItemsMap.get(pid)?.quantity;
    return guestBucketMap.get(pid);
  };

  const handleToggleSave = async (item, shouldSave) => {
    if (!customerId) {
      navigate('/consumer');
      return false;
    }
    const pid = String(item.product_id || item._id || item.id);
    try {
      if (shouldSave) {
        await addItemToSavedMutation.mutateAsync({ customer_id: customerId, product_id: pid });
      } else {
        await removeItemFromSavedMutation.mutateAsync({ customer_id: customerId, product_id: pid });
      }
      return true;
    } catch {
      return false;
    }
  };

  const handleOpenOptions = async (productItem, isBuyNowAvailable) => {
    const pid = String(productItem.product_id || productItem._id);
    try {
      const purchase_mode = isBuyNowAvailable ? 'Buy Now' : 'Quote';
      const variantsResponse = await fetchProductVariants({ product_id: pid, purchase_mode });
      const list = Array.isArray(variantsResponse)
        ? variantsResponse
        : variantsResponse?.product_variants || [];

      setSelectedProductWithOptions(list);
      setIsOptionsAvailableForBuyNow(isBuyNowAvailable);
      setIsOptionsSheetVisible(true);
    } catch (error) {
      console.error('Error fetching variants:', error);
    }
  };

  // 3. Scroll Boundary Observers
  const checkScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    const hasOverflow = maxScroll > 5;
    const isAtStart = scrollLeft <= 5;
    const isAtEnd = scrollLeft >= maxScroll - 5;

    setCanScrollLeft(!isAtStart && hasOverflow);
    setCanScrollRight(!isAtEnd && hasOverflow);
  }, []);

  useEffect(() => {
    checkScrollState();
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener('scroll', checkScrollState, { passive: true });
    window.addEventListener('resize', checkScrollState);

    const observer = new ResizeObserver(checkScrollState);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', checkScrollState);
      window.removeEventListener('resize', checkScrollState);
      observer.disconnect();
    };
  }, [checkScrollState, products]);

  const handleScroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollAmount = Math.max(el.clientWidth * 0.75, 240);
    const targetScroll =
      direction === 'left' ? el.scrollLeft - scrollAmount : el.scrollLeft + scrollAmount;

    el.scrollTo({ left: targetScroll, behavior: 'smooth' });
  };

  if (isLoading || !products || products.length === 0) return null;

  return (
    <section style={styles.sectionWrap}>
      <style>{`
        .bb-rail-outer-shell {
          position: relative;
          width: 100%;
          display: flex;
          align-items: center;
        }

        .bb-product-rail-scroll {
          display: flex;
          overflow-x: auto;
          overflow-y: visible;
          gap: 16px;
          padding: 6px 2px 18px 2px;
          scroll-snap-type: x mandatory;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
          width: 100%;
          box-sizing: border-box;
          scroll-behavior: smooth;
        }

        .bb-product-rail-scroll::-webkit-scrollbar {
          display: none;
        }

        .bb-product-rail-card-wrapper {
          width: 176px;
          min-width: 176px;
          flex-shrink: 0;
          scroll-snap-align: start;
          box-sizing: border-box;
        }

        .bb-rail-nav-btn {
          position: absolute;
          top: calc(50% - 10px);
          transform: translateY(-50%);
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background-color: #FFFFFF;
          border: 1px solid #CBD5E1;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.14);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
          transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
          padding: 0;
        }

        .bb-rail-nav-btn:hover {
          background-color: #F8FAFC;
          box-shadow: 0 6px 16px rgba(15, 23, 42, 0.22);
          transform: translateY(-50%) scale(1.05);
        }

        .bb-rail-nav-btn:active {
          transform: translateY(-50%) scale(0.95);
        }

        .bb-rail-nav-left {
          left: -14px;
        }

        .bb-rail-nav-right {
          right: -14px;
        }

        @media (max-width: 768px) {
          .bb-rail-nav-btn {
            display: none !important;
          }
          .bb-product-rail-card-wrapper {
            width: 160px;
            min-width: 160px;
          }
        }

        @media (max-width: 480px) {
          .bb-product-rail-card-wrapper {
            width: 146px;
            min-width: 146px;
          }
          .bb-product-rail-scroll {
            gap: 12px;
          }
        }
      `}</style>

      {/* Header */}
      <div style={styles.headerRow}>
        <div>
          <h3 style={styles.titleText}>{title}</h3>
          {subtitle && <span style={styles.subtitleText}>{subtitle}</span>}
        </div>
      </div>

      {/* Product Rail with Progressive Scroll Buttons */}
      <div className="bb-rail-outer-shell">
        {canScrollLeft && (
          <button
            type="button"
            className="bb-rail-nav-btn bb-rail-nav-left"
            onClick={() => handleScroll('left')}
            aria-label="Scroll backward"
          >
            <img
              src={CaretRight}
              alt="‹"
              style={{ width: 14, height: 14, transform: 'rotate(180deg)' }}
            />
          </button>
        )}

        <div ref={scrollRef} className="bb-product-rail-scroll">
          {products.map((prod) => {
            const pid = String(prod.product_id || prod._id);
            const qty = getProductQuantity(pid);
            const bucketEntry = customerId ? bucketItemsMap.get(pid) : null;

            return (
              <div key={pid} className="bb-product-rail-card-wrapper">
                <ProductCard
                  item={prod}
                  customerId={customerId}
                  hubId={hubId}
                  savedBucketQuantity={qty}
                  bucketId={bucketEntry?.bucketId}
                  isSavedItem={savedItemsSet.has(pid)}
                  onToggleSave={handleToggleSave}
                  onOptionsClick={handleOpenOptions}
                  onItemClick={() =>
                    navigate('/consumer/productdetails', { state: { productId: pid } })
                  }
                />
              </div>
            );
          })}
        </div>

        {canScrollRight && (
          <button
            type="button"
            className="bb-rail-nav-btn bb-rail-nav-right"
            onClick={() => handleScroll('right')}
            aria-label="Scroll forward"
          >
            <img src={CaretRight} alt="›" style={{ width: 14, height: 14 }} />
          </button>
        )}
      </div>

      <ProductOptionsModal
        visible={isOptionsSheetVisible}
        onClose={() => setIsOptionsSheetVisible(false)}
        cardTitle={isOptionsAvailableForBuyNow ? 'Add To Buy' : 'Add To Quote'}
        productData={selectedProductWithOptions}
        bucketItemsMap={bucketItemsMap}
        customerId={customerId}
        hubId={hubId}
        purchaseType={isOptionsAvailableForBuyNow ? 'BUY_NOW' : 'RFQ'}
      />
    </section>
  );
}

const styles = {
  sectionWrap: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 20,
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: 4,
  },
  titleText: {
    fontSize: 'clamp(16px, 2vw, 19px)',
    fontWeight: '800',
    color: '#0F172A',
    margin: 0,
    letterSpacing: '-0.3px',
  },
  subtitleText: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 2,
    display: 'block',
  },
};